import { apiUrl } from "../utils/api";
import { apiFetch } from "../utils/apiFetch";
import { canonicalFirmaConn } from "../components/relationsInline/types";

// ── Typen ─────────────────────────────────────────────────────────────────────

export type StructureStatus = "open" | "erledigt";
export type StructureSort = "count" | "street";

/** Eine Adress-Gruppe in der Übersicht. */
export interface StructureListRow {
  structureKey: string;
  companyCount: number;
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  /** true = mind. eine Firma noch nicht geprüft. */
  hasOpen: boolean;
  /** Bis zu 6 Firmennamen für die Karten-Vorschau. */
  names: string[];
}

/** Eine bestehende Firma↔Firma-Verbindung (roh, beide Richtungen). */
export interface StructureConnection {
  id: number;
  contactSrcId: number;
  contactDestId: number;
  relationType: string;
  share: string | null;
  srcName: string | null;
  destName: string | null;
}

export type StructureReviewStatus = "linked" | "not_belongs" | null;

/** Eine Firma innerhalb einer Adress-Gruppe. */
export interface StructureCompany {
  id: number;
  name: string | null;
  suffix: string | null;
  contactNbrInt: string | null;
  industry: string | null;
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  reviewStatus: StructureReviewStatus;
  connections: StructureConnection[];
}

export interface StructureDetail {
  structureKey: string;
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  companies: StructureCompany[];
}

/** Marker-Update je Firma (null = zurück auf offen). */
export interface StructureReviewItem {
  contactId: number;
  status: StructureReviewStatus;
}

// ── Reads ─────────────────────────────────────────────────────────────────────

/** Übersicht (Adress-Gruppen) + total für den Header. */
export const listStructures = async (
  status: StructureStatus,
  sort: StructureSort,
  teamId: string | undefined,
  q: string | undefined,
  limit: number,
  offset: number,
  signal?: AbortSignal,
): Promise<{ rows: StructureListRow[]; total: number }> => {
  const { data, total } = await apiFetch<StructureListRow[]>("unternehmensstrukturen-cleanup", {
    params: {
      status,
      sort,
      ...(teamId ? { teamId } : {}),
      ...(q ? { q } : {}),
      limit,
      offset,
    },
    signal,
  });
  return { rows: data ?? [], total: total ?? data?.length ?? 0 };
};

/** Detail einer Adresse: alle Firmen + bestehende Verbindungen + Review-Status. */
export const fetchStructureDetail = async (structureKey: string): Promise<StructureDetail> => {
  const { data } = await apiFetch<StructureDetail>("unternehmensstrukturen-cleanup/detail", {
    params: { structureKey },
  });
  return data;
};

/** BO-Teams für den „Verantwortlich"-Filter. */
export const listStructureTeams = async (): Promise<Array<{ id: string; name: string | null }>> => {
  const { data } = await apiFetch<Array<{ id: string; name: string | null }>>(
    "unternehmensstrukturen-cleanup/teams",
  );
  return data ?? [];
};

// ── Writes ────────────────────────────────────────────────────────────────────

async function postJson(path: string, body: unknown): Promise<any> {
  const res = await fetch(apiUrl(path), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `Speichern fehlgeschlagen: HTTP ${res.status}`;
    try {
      const d = await res.json();
      if (d?.message) msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json();
}

/**
 * Firmenverbindung anlegen — kanonisiert über {@link canonicalFirmaConn} (flippt
 * „Mutterfirma" → „Tochterfirma") und ruft den bestehenden `POST /contact-relation`
 * (idempotenter Upsert; erzeugt bei „Tochterfirma" automatisch die Schwester-Kanten).
 */
export const createFirmaConnection = async (
  role: string,
  selfId: number,
  otherId: number,
  share?: string | null,
): Promise<any> => {
  const canon = canonicalFirmaConn(role, selfId, otherId);
  return postJson("contact-relation", {
    ...canon,
    share: share && share.trim() ? share.trim() : undefined,
    isExtern: false,
    sameHousehold: false,
  });
};

/** Review-Marker eines Clusters speichern (linked / not_belongs / zurücksetzen). */
export const saveStructureReview = async (
  structureKey: string,
  items: StructureReviewItem[],
): Promise<{ processed: number }> => {
  return postJson("unternehmensstrukturen-cleanup/save", { structureKey, items });
};
